import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import GenericInput from "src/components/input-components/GenericTextInput";
import GenericSelectInput from "src/components/input-components/GenericSelectInput";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ImagePicker from "src/components/input-components/GenericImagePicker";

// --- Type Definitions ---
export interface IProjectsListingFilter {
  Title: string;
  Properties: {
    Filter: string;
  };
}

export interface IProjectsListingItem {
  Title: string;
  Subtitle: string;
  Category: string;
  ImageUrl: string;
  Link: string;
}

export interface IProjectsListingWidget {
  type: string; // Should be WidgetType.ProjectsListing
  Title: string;
  Filters: IProjectsListingFilter[];
  Items: IProjectsListingItem[];
}

// --- Props for the ProjectsListingItem component ---
interface ProjectsListingItemProps {
  item: IProjectsListingWidget;
  error: any;
  onChange: (updatedItem: IProjectsListingWidget, field: string) => void;
  onblur: (field: string, index?: number) => void;
}

const ProjectsListingItem: React.FC<ProjectsListingItemProps> = ({
  item,
  error,
  onChange,
  onblur,
}) => {
  // Overall widget title.
  const widgetTitle: string = typeof item.Title === "string" ? item.Title : "";

  // Ensure Filters and Items are arrays.
  const filters: IProjectsListingFilter[] = Array.isArray(item.Filters) ? item.Filters : [];
  const projects: IProjectsListingItem[] = Array.isArray(item.Items) ? item.Items : [];

  // Helper: extract value from event or direct string.
  const getValue = (e: React.ChangeEvent<HTMLInputElement> | string): string =>
    typeof e === "string" ? e : e.target.value;

  // --- Overall Title Handler ---
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement> | string) => {
    onChange({ ...item, Title: getValue(e) }, "Title");
  };

  // --- Filters Handlers ---
  const handleFilterChange = (
    index: number,
    field: "Title" | "Filter",
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const newValue = getValue(e);
    const updatedFilters = [...filters];
    if (field === "Filter") {
      const currentFilter = updatedFilters[index];
      updatedFilters[index] = {
        ...currentFilter,
        Properties: { ...currentFilter.Properties, Filter: newValue },
      };
    } else {
      updatedFilters[index] = { ...updatedFilters[index], Title: newValue };
    }
    onChange({ ...item, Filters: updatedFilters }, "Filters");
  };

  const handleAddFilter = () => {
    const newFilter: IProjectsListingFilter = { Title: "", Properties: { Filter: "" } };
    const updatedFilters = [...filters, newFilter];
    onChange({ ...item, Filters: updatedFilters }, "Filters");
  };

  const handleRemoveFilter = (index: number) => {
    const updatedFilters = filters.filter((_, i) => i !== index);
    onChange({ ...item, Filters: updatedFilters }, "Filters");
  };

  // --- Projects Handlers ---
  const handleProjectChange = (
    index: number,
    field: keyof IProjectsListingItem,
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const newValue = getValue(e);
    const updatedProjects = [...projects];
    updatedProjects[index] = { ...updatedProjects[index], [field]: newValue };
    onChange({ ...item, Items: updatedProjects }, "Items");
  };

  const handleAddProject = () => {
    const newProject: IProjectsListingItem = {
      Title: "",
      Subtitle: "",
      Category: "",
      ImageUrl: "",
      Link: "",
    };
    const updatedProjects = [...projects, newProject];
    onChange({ ...item, Items: updatedProjects }, "Items");
  };

  const handleRemoveProject = (index: number) => {
    const updatedProjects = projects.filter((_, i) => i !== index);
    onChange({ ...item, Items: updatedProjects }, "Items");
  };

  // --- Image Picker for Project Image ---
  const [projectImagePickerOpen, setProjectImagePickerOpen] = useState<boolean>(false);
  const [currentProjectIndex, setCurrentProjectIndex] = useState<number | null>(null);

  const handleOpenProjectImagePicker = (index: number) => {
    setCurrentProjectIndex(index);
    setProjectImagePickerOpen(true);
  };

  const handleProjectImageSelect = (selected: string | string[]) => {
    if (currentProjectIndex !== null) {
      const newImage = Array.isArray(selected) ? selected[0] : selected;
      const updatedProjects = [...projects];
      updatedProjects[currentProjectIndex] = {
        ...updatedProjects[currentProjectIndex],
        ImageUrl: newImage,
      };
      onChange({ ...item, Items: updatedProjects }, "Items");
    }
    setProjectImagePickerOpen(false);
    setCurrentProjectIndex(null);
  };

  return (
    <Card elevation={2} sx={{ p: 2, mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Projects Listing Widget
        </Typography>
        {/* Overall Widget Title */}
        <GenericInput
          type="text"
          name="Title"
          title="Widget Title"
          value={widgetTitle}
          error={error?.Title || ""}
          onChange={(e) => handleTitleChange(e)}
          onBlur={() => onblur("Title")}
          disabled={false}
        />

        {/* Filters Section */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Filters
        </Typography>
        {filters.map((filter, index) => (
          <Box
            key={index}
            sx={{
              border: "1px solid #ddd",
              p: 2,
              borderRadius: "4px",
              mb: 2,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={5}>
                <GenericInput
                  type="text"
                  name={`FilterTitle-${index}`}
                  title="Filter Title"
                  value={filter.Title}
                  error={error?.[index]?.Title || ""}
                  onChange={(e) => handleFilterChange(index, "Title", e)}
                  onBlur={() => onblur("Title", index)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <GenericInput
                  type="text"
                  name={`Filter-${index}`}
                  title="Filter"
                  value={filter.Properties.Filter}
                  error={error?.[index]?.Filter || ""}
                  onChange={(e) => handleFilterChange(index, "Filter", e)}
                  onBlur={() => onblur("Filter", index)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <IconButton color="error" onClick={() => handleRemoveFilter(index)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddFilter}>
          Add Filter
        </Button>

        {/* Projects Section */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Projects
        </Typography>
        {projects.map((proj, index) => (
          <Box
            key={index}
            sx={{
              border: "1px solid #ddd",
              p: 2,
              borderRadius: "4px",
              mb: 2,
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <GenericInput
                  type="text"
                  name={`ProjTitle-${index}`}
                  title="Project Title"
                  value={proj.Title}
                  error={error?.[index]?.Title || ""}
                  onChange={(e) => handleProjectChange(index, "Title", e)}
                  onBlur={() => onblur("Title", index)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericInput
                  type="text"
                  name={`ProjSubtitle-${index}`}
                  title="Project Subtitle"
                  value={proj.Subtitle}
                  error={error?.[index]?.Subtitle || ""}
                  onChange={(e) => handleProjectChange(index, "Subtitle", e)}
                  onBlur={() => onblur("Subtitle", index)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericInput
                  type="text"
                  name={`Category-${index}`}
                  title="Category"
                  value={proj.Category}
                  error={error?.[index]?.Category || ""}
                  onChange={(e) => handleProjectChange(index, "Category", e)}
                  onBlur={() => onblur("Category", index)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {/* Use Image Picker for the ImageUrl */}
                <Box
                  sx={{
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    height: 100,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    backgroundColor: "#f9f9f9",
                  }}
                  onClick={() => handleOpenProjectImagePicker(index)}
                >
                  {proj.ImageUrl ? (
                    <img
                      src={proj.ImageUrl}
                      alt={`Project ${index + 1}`}
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Select Image
                    </Typography>
                  )}
                </Box>
                {error?.[index]?.ImageUrl && (
                  <Typography variant="body2" color="error">
                    {error?.[index]?.ImageUrl}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericInput
                  type="text"
                  name={`Link-${index}`}
                  title="Project Link"
                  value={proj.Link}
                  error={error?.[index]?.Link || ""}
                  onChange={(e) => handleProjectChange(index, "Link", e)}
                  onBlur={() => onblur("Link", index)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <IconButton color="error" onClick={() => handleRemoveProject(index)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddProject}>
          Add Project
        </Button>
      </CardContent>
      {projectImagePickerOpen && (
        <ImagePicker
          open={projectImagePickerOpen}
          multiSelect={false}
          onClose={() => setProjectImagePickerOpen(false)}
          onSelect={handleProjectImageSelect}
        />
      )}
    </Card>
  );
};

export default ProjectsListingItem;
